import React, { useState } from 'react';
import { Mail, MapPin, CheckCircle2 } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    topic: 'AI Tools',
    budget: '',
    message: '',
    requestType: '30-min discovery call'
  });
  
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          organization: formData.organization,
          email: formData.email,
          topic: formData.topic,
          budget: formData.budget,
          message: formData.message,
          request_type: formData.requestType
        })
      });

      if (response.ok) {
        console.log('Form submitted successfully');
        setSubmitted(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setSubmitted(false);
          setFormData({
            name: '',
            organization: '',
            email: '',
            topic: 'AI Tools',
            budget: '',
            message: '',
            requestType: '30-min discovery call'
          });
        }, 3000);
      } else {
        console.error('Form submission failed');
        alert('There was an error submitting the form. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting the form. Please try again.');
    }
  };

  return (
    <div style={{ paddingTop: '80px', background: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 className="display-huge" style={{ marginBottom: '24px' }}>
            Let's Talk
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
            Ready to explore AI tools or climate strategy? Book a call and let's discuss your goals.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section style={{ padding: '60px 7.6923% 100px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px' }}>
          {/* Contact Info */}
          <div>
            <h2 className="display-medium" style={{ marginBottom: '32px' }}>
              Get in Touch
            </h2>
            <p className="body-medium" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
              Whether you need AI solutions, climate consultancy, or both—we're here to help. Fill out the form and we'll get back to you within 24 hours.
            </p>
            
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'start', gap: '16px', marginBottom: '24px' }}>
                <MapPin size={24} style={{ color: 'var(--brand-primary)', marginTop: '4px' }} />
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                    Office
                  </h3>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                    Amsterdam, Netherlands
                  </p>
                </div>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'start', gap: '16px', marginBottom: '24px' }}>
                <Mail size={24} style={{ color: 'var(--brand-primary)', marginTop: '4px' }} />
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                    Email
                  </h3>
                  <a
                    href="mailto:info@futura-softwares.com"
                    className="body-medium"
                    style={{ color: 'var(--text-secondary)', textDecoration: 'none', transition: 'color 0.3s ease' }}
                    onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                    onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                  >
                    info@futura-softwares.com
                  </a>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand-primary)', marginTop: '4px' }}><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '8px' }}>
                    Website
                  </h3>
                  <a
                    href="https://www.futura-softwares.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="body-medium"
                    style={{ color: 'var(--text-secondary)', textDecoration: 'none', transition: 'color 0.3s ease' }}
                    onMouseEnter={(e) => e.target.style.color = 'var(--brand-primary)'}
                    onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                  >
                    www.futura-softwares.com
                  </a>
                </div>
              </div>
            </div>

            <div style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)',
              padding: '32px',
              marginTop: '40px'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '16px' }}>
                What to Expect
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Response within 24 hours',
                  'Initial consultation (no sales pitch)',
                  'Clear next steps and timelines',
                  'Transparent pricing and scope'
                ].map((item, idx) => (
                  <li key={idx} style={{
                    fontSize: '16px',
                    color: 'var(--text-secondary)',
                    marginBottom: '12px',
                    paddingLeft: '28px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      color: 'var(--brand-primary)'
                    }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            padding: '40px'
          }}>
            {submitted ? (
              <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                <CheckCircle2 size={64} style={{ color: 'var(--brand-primary)', marginBottom: '24px' }} />
                <h3 className="heading-2" style={{ marginBottom: '16px' }}>
                  Thank you!
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  We've received your message and will get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none',
                      transition: 'border-color 0.3s ease'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Organization *
                  </label>
                  <input
                    type="text"
                    name="organization"
                    value={formData.organization}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Topic *
                  </label>
                  <select
                    name="topic"
                    value={formData.topic}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  >
                    <option value="AI Tools">AI Tools & Agents</option>
                    <option value="HFC & CO₂ Strategy">HFC & CO₂ Strategy</option>
                    <option value="Both">Both</option>
                  </select>
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Budget Range (optional)
                  </label>
                  <input
                    type="text"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    placeholder="e.g., €50k-100k"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none',
                      resize: 'vertical'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  />
                </div>

                <div style={{ marginBottom: '32px' }}>
                  <label className="body-small" style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                    I'd like to request:
                  </label>
                  <select
                    name="requestType"
                    value={formData.requestType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: 'var(--bg-primary)',
                      border: '1px solid var(--border-subtle)',
                      color: 'var(--text-primary)',
                      fontSize: '16px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--brand-primary)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle)'}
                  >
                    <option value="30-min discovery call">30-min discovery call</option>
                    <option value="60-min strategy workshop">60-min strategy workshop</option>
                  </select>
                </div>

                <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                  Send Message
                </button>
              </form>
            )}
          </div>
        </div>

        <style>{`
          @media (max-width: 1024px) {
            section > div {
              grid-template-columns: 1fr !important;
              gap: 40px !important;
            }
          }
        `}</style>
      </section>
    </div>
  );
};

export default Contact;
